import { action, computed, observable } from "mobx";
import moment from "moment";
import JobType from "~/constants/PhotoEditing/JobType";
import JobStatus from "~/constants/PhotoEditing/JobStatus";
import PhotoEditingImage from "./PhotoEditingImage";
import PhotoEditingLabel from "./PhotoEditingLabel";
import Provider from "../Provider";

class PhotoEditingJob {
  /**
   * @property {number|null}
   */
  @observable photoEditingJobId = null;

  /**
   * @property {number|null}
   */
  @observable providerId = null;

  /**
   * @property {number|null}
   */
  @observable tourId = null;

  /**
   * @property {number} typeId Either an HDR Real job or a Basic HDR job.
   */
  @observable typeId = JobType.HD_REAL;

  /**
   * @property {number} imagesCount cached amount of images for this job
   */
  @observable imagesCount = 0;

  /**
   * @property {array} photoEditingImages actual images instances
   */
  @observable photoEditingImages = [];

  /**
   * @property {string[]} photoEditingLabels an array of labels
   */
  @observable photoEditingLabels = [];

  /**
   * @property {number}
   */
  @observable photosPerImage = 0;

  /**
   * @property {number} creationDts timestamp When the job was created
   */
  @observable creationDts = null;

  /**
   * @property {number} startedDts timestamp when the job began to be processed (e.g merge hdr photos)
   */
  @observable startedDts = null;

  /**
   * @property {number} finishedDts timestamp when the job processing finished
   */
  @observable finishedDts = null;

  /**
   * @property {number} estimatedTime An approximate time in seconds that the job would take to be finished.
   * Calculated and set in the backend once a job has started, given by the amount of photos in the job.
   *
   */
  @observable estimatedTime = 0;

  /**
   * @property {number} estimatedTimeLeftToFinish When a job has started, we'll show an approximated time
   * to be completed, given the ETA.
   */
  @observable estimatedTimeLeftToFinish = null;

  /**
   * This is set at runtime. Used as a helper for storing the countdown function that updates the time left
   * to complete a job (if in progress)
   */
  estimatedTimeLeftToFinishIntervalId = null;

  /**
   * @property {Array}
   */
  @observable refunds = [];

  /**
   * @property {Boolean}
   */
  @observable isEntitledToFreeProduct = false;

  /**
   * @property {number}
   */
  @observable delayedDts = null;

  /**
   * @property {Provider|null}
   */
  @observable provider = null;

  constructor({
    photoEditingJobId = null,
    providerId = null,
    tourId = null,
    typeId = JobType.HD_REAL,
    estimatedTime = 0,
    photosPerImage = 0,
    creationDts = null,
    startedDts = null,
    finishedDts = null,
    photoEditingImages = [],
    photoEditingLabels = [],
    refunds = [],
    isEntitledToFreeProduct = false,
    delayedDts = null,
    provider = null,
  }) {
    this.photoEditingJobId = photoEditingJobId;
    this.providerId = providerId;
    this.tourId = tourId;
    // We need to normalize timestamps since PHP doesn't account for milliseconds but javascript does
    this.creationDts = creationDts * 1000;
    // cast null to 0
    this.typeId = typeId || 0;
    this.estimatedTime = estimatedTime || 0;
    this.photosPerImage = photosPerImage || 0;
    this.startedDts = startedDts ? startedDts * 1000 : null;
    this.finishedDts = finishedDts ? finishedDts * 1000 : null;
    this.delayedDts = delayedDts ? delayedDts * 1000 : null;

    photoEditingImages.forEach(photoEditingImage =>
      this.photoEditingImages.push(new PhotoEditingImage(photoEditingImage)),
    );

    this.photoEditingImages = this.photoEditingImages.slice().sort((a, b) => (a.creationDts > b.creationDts ? 1 : -1));

    photoEditingLabels.forEach(photoEditingLabel => {
      this.photoEditingLabels.push(new PhotoEditingLabel(photoEditingLabel));
    });

    this.setRefunds(refunds);

    this.estimatedTimeLeftToFinish = this.calculateEstimatedTimeLeftToFinish();
    this.isEntitledToFreeProduct = isEntitledToFreeProduct;

    if (provider) {
      this.provider = new Provider(provider);
    }
  }

  @computed
  get typeLabel() {
    switch (this.typeId) {
      case JobType.HD_REAL:
        return "HDReal®";
      // no break
      case JobType.BASIC_HDR:
        return "HDR";
      // no break
      default:
        throw new Error(`Unknown job type ${this.type}`);
    }
  }

  @computed
  get isTypeHDReal() {
    return this.typeId === JobType.HD_REAL;
  }

  @computed
  get isTypeBasicHDR() {
    return this.typeId === JobType.BASIC_HDR;
  }

  /**
   * @returns {string}
   */
  @computed
  get estimatedTimeLabel() {
    if (this.estimatedTime === 0) {
      return "N/A";
    }

    return moment.duration(this.estimatedTime, "seconds").humanize();
  }

  /**
   * The actual time it took for the job to be processed.
   * @TODO not sure if this should be formatting the timestamp. See tickEstimatedTimeLeftToFinish()
   */
  @computed
  get timeTookToFinish() {
    // job must be done (note we expect that startDts is always set when finishedDts is set as well)
    if (!this.finishedDts) {
      return "";
    }

    // calculate the time it took to finish.
    const processingTime = this.finishedDts - this.startedDts;

    return moment.duration(processingTime, "milliseconds").humanize();
  }

  /**
   * @returns {Number} estimated time left to finish, in seconds
   */
  calculateEstimatedTimeLeftToFinish() {
    // Estimated time left to finish only makes sense when job is in progress.
    if (!this.isInProgress()) {
      return 0;
    }

    // If there's no estimated time to finish then it's not possible to do a calculation.
    if (!this.estimatedTime) {
      return 0;
    }

    // Calculate the seconds left for the estimated finished time.
    const startedDate = new Date(this.startedDts);
    // Note that JS date timestamp is in milliseconds but the eta property is in seconds.
    const estimatedFinishTimestamp = startedDate.getTime() + this.estimatedTime * 1000;

    // Note this value could be negative if the ETA is already up (in the past).
    // It's up the caller to handle it.
    const millisecondsLeft = estimatedFinishTimestamp - Date.now();

    return parseInt(millisecondsLeft / 1000, 10);
  }

  /**
   * Creates a timer that updates the estimatedTimeLeftToFinish
   * @TODO this should only calculate the timestamp (int), date string formatting should happen at the
   * components.
   */
  @action
  tickEstimatedTimeLeftToFinish() {
    const tick = () => {
      // calculate upfront. the calculateEstimatedTimeLeftToFinish() method will take care of invalid states.
      this.estimatedTimeLeftToFinish = this.calculateEstimatedTimeLeftToFinish();

      // "time left" only makes sense when in progress
      if (!this.isInProgress()) {
        clearInterval(this.estimatedTimeLeftToFinishIntervalId);
      }

      // if there's no estimated time to finish then it's not possible to calculate estimated time left.
      if (!this.estimatedTime) {
        clearInterval(this.estimatedTimeLeftToFinishIntervalId);
      }

      // ETA is in the past, this job should finish any time soon, so no need to keep ticking.
      if (this.estimatedTimeLeftToFinish <= 0) {
        clearInterval(this.estimatedTimeLeftToFinishIntervalId);
      }
    };

    // tick upfront
    tick();
    // Start ticking each 50 seconds (instead of 60) to avoid overlaps.
    this.estimatedTimeLeftToFinishIntervalId = setInterval(tick, 50 * 1000);
  }

  @computed
  get estimatedTimeLeftToFinishLabel() {
    // If negative value then the ETA has been already met and it's in the past.
    if (this.estimatedTimeLeftToFinish <= 0) {
      clearInterval(this.estimatedTimeLeftToFinishIntervalId);

      return "a few seconds";
    }

    return moment.duration(this.estimatedTimeLeftToFinish, "seconds").humanize();
  }

  isInProgress() {
    return this.startedDts !== null && this.finishedDts === null;
  }

  /**
   * @returns {string}
   */
  @computed
  get status() {
    if (this.finishedDts) {
      return JobStatus.DONE;
    }

    if (this.startedDts) {
      return JobStatus.IN_PROGRESS;
    }

    return JobStatus.IN_CREATION;
  }

  @computed
  get isStatusInCreation() {
    // not started and not finished
    return !this.startedDts && !this.finishedDts;
  }

  @computed
  get isStatusDone() {
    return this.finishedDts !== null;
  }

  @computed
  get isStatusInProgress() {
    // started but not finished
    return this.startedDts !== null && this.finishedDts == null;
  }

  /**
   * @returns {string}
   */
  @computed
  get statusLabel() {
    if (this.finishedDts && this.withErrorMessagesButNotRefundedPhotoEditingImages.length === 0) {
      return "Done";
    }

    if (this.finishedDts) {
      return "Processed with errors";
    }

    if (this.startedDts) {
      return "In Progress";
    }

    return "In Creation";
  }

  /**
   * @returns {Array}
   */
  @computed
  get refundedPhotoEditingImages() {
    return this.photoEditingImages.filter(photoEditingImage => photoEditingImage.refundDts !== null);
  }

  /**
   * @returns {Number}
   */
  @computed
  get numberOfProducts() {
    const numberOfImages = this.photoEditingImages.length;

    if (this.typeId === JobType.HD_REAL) {
      return numberOfImages;
    }

    let numberOfProducts = 1;

    // From 51 and up, we will charge a package for every 50 images
    if (numberOfImages > 50) {
      numberOfProducts = 1 + parseInt((numberOfImages - 1) / 50, 10);
    }

    return numberOfProducts;
  }

  /**
   * @returns {Number}
   */
  @computed
  get numberOfFreeProducts() {
    // We were storing the refund information at the image level before
    // Because one image was 1 product
    // This is to support the refund of an image
    if (this.typeId === JobType.HD_REAL && this.refunds.length === 0) {
      return this.refundedPhotoEditingImages.length;
    }

    if (this.isEntitledToFreeProduct) {
      return 1;
    }

    const freeProducts = this.refunds.filter(
      imagePackage => imagePackage.isFree === true || imagePackage.refundDts !== null,
    );

    return freeProducts.length;
  }

  /**
   * @returns {Array}
   */
  @computed
  get withErrorMessagesButNotRefundedPhotoEditingImages() {
    return this.photoEditingImages.filter(
      photoEditingImage => photoEditingImage.messages.length > 0 && photoEditingImage.refundDts === null,
    );
  }

  /**
   * @param {string}
   */
  @action
  addPhotoEditingLabel(photoEditingLabel) {
    this.photoEditingLabels.push(new PhotoEditingLabel(photoEditingLabel));
  }

  /**
   * @param {integer} photoEditingLabelId
   */
  removePhotoEditingLabelById(photoEditingLabelId) {
    const label = this.photoEditingLabels.find(labelLookup => labelLookup.photoEditingLabelId === photoEditingLabelId);

    if (!label) {
      throw new Error(`Could not find photoEditingLabel by id: ${photoEditingLabelId}`);
    }

    const indexAt = this.photoEditingLabels.indexOf(label);
    this.photoEditingLabels.splice(indexAt, 1);
  }

  /**
   * Get a list of status options consumable by a Dropdown component.
   */
  static getStatusOptions() {
    return [
      { key: "any", value: "", text: "Any" },
      { key: JobStatus.IN_CREATION, value: JobStatus.IN_CREATION, text: "In Creation" },
      { key: JobStatus.IN_PROGRESS, value: JobStatus.IN_PROGRESS, text: "In Progress" },
      { key: JobStatus.DONE, value: JobStatus.DONE, text: "Done" },
    ];
  }

  /**
   * Get a list of status options consumable by a Dropdown component.
   */
  static getAdminStatusOptions() {
    return [
      { key: "any", value: "", text: "Any" },
      { key: "1,2", value: "1,2", text: "Not In Creation" },
      { key: JobStatus.IN_CREATION, value: JobStatus.IN_CREATION, text: "In Creation" },
      { key: JobStatus.IN_PROGRESS, value: JobStatus.IN_PROGRESS, text: "In Progress" },
      { key: JobStatus.DONE, value: JobStatus.DONE, text: "Done" },
    ];
  }

  /**
   * Set refunds
   *
   * @param {Array|null} refunds
   */
  @action
  setRefunds(refunds = []) {
    this.refunds = [];

    if (refunds !== null) {
      refunds.forEach(refund => this.refunds.push(refund));
    }
  }
}

export default PhotoEditingJob;
